import type { FeeEntry } from '../model/Specialist';
import { CoverType } from '../model/MedicareCard';

export interface Range {
  min: number;
  max: number;
}

export type FeeBasis = 'medicare-assumed' | 'medicare' | 'medicare-to-follow' | 'dva' | 'none';

export interface FeeEstimate {
  basis: FeeBasis;
  total: number;
  // Null when no rebate applies (no card, or no rebate set for the clinician).
  rebate: Range | null;
  gap: Range;
}

// Admin stores amounts as strings ("Integer and decimal only"); the API may also send numbers.
const toAmount = (value?: string | number): number | null => {
  if (value === undefined || value === null || String(value).trim() === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
};

const roundCents = (amount: number) => Math.round(amount * 100) / 100;

// Rebate range as admin configures it on the practitioner profile: Min, Max (defaults to Min),
// falling back to the legacy single medicareRebate for profiles not yet migrated.
export const getRebateRange = (fee: FeeEntry): Range | null => {
  const min = toAmount(fee.medicareRebateMin) ?? toAmount(fee.medicareRebate);
  if (min === null) return null;
  const max = toAmount(fee.medicareRebateMax) ?? min;
  return max >= min ? { min, max } : { min: max, max: min };
};

const BASIS: Record<CoverType, FeeBasis> = {
  [CoverType.MEDICARE]: 'medicare',
  [CoverType.MEDICARE_NOT_WITH_ME]: 'medicare-to-follow',
  [CoverType.DVA]: 'dva',
  [CoverType.NONE]: 'none',
};

// Out of pocket is always total − rebate range; admin's own "out of pocket" figure is not used.
// Medicare and DVA both claim the rebate; until the patient chooses their cover, Medicare is assumed.
export const getFeeEstimate = (fee: FeeEntry | undefined, cover?: CoverType): FeeEstimate | null => {
  const total = toAmount(fee?.total);
  if (!fee || total === null) return null;
  const basis = cover ? BASIS[cover] : 'medicare-assumed';

  const rebate = getRebateRange(fee);
  if (basis === 'none' || !rebate) {
    return {
      basis, total, rebate: null, gap: { min: total, max: total },
    };
  }
  return {
    basis,
    total,
    rebate,
    gap: {
      min: roundCents(Math.max(0, total - rebate.max)),
      max: roundCents(Math.max(0, total - rebate.min)),
    },
  };
};

export const formatMoney = (amount: number) => `$${amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// "$244.05 – $268.10", or one amount when both ends match.
export const formatRange = ({ min, max }: Range) => (
  min === max ? formatMoney(min) : `${formatMoney(min)} – ${formatMoney(max)}`
);
